import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  sendOtpAction,
  verifyOtpAction,
  VerifyOtpResponse,
} from '@percent/hosted-validation-form/api/actions/otpActions';
import { ClientResponse } from '@percent/hosted-validation-form/api/types/types';
import { toAxiosError } from '@percent/hosted-validation-form/utils/toAxiosError';
import { ValidationSubmissionArguments } from '@percent/hosted-validation-form/api/actions/validationSubmissionAction';
import { useMutation } from '../useMutation/useMutation';
import { useOtpState } from '../useOtpState/useOtpState';

export const OTP_COOLDOWN_SECONDS = 30;

export const OTP_MAX_ATTEMPTS_EXCEEDED_CODE = 'validation_invite/otp_max_attempts_exceeded';

export type OtpErrorKey =
  | 'hostedValidation.otp.errors.invalidCode'
  | 'hostedValidation.otp.errors.expired'
  | 'hostedValidation.otp.errors.rateLimit'
  | 'hostedValidation.otp.errors.generic';

const isMaxAttemptsExceededError = (error: unknown): boolean =>
  toAxiosError(error)?.response?.data?.error?.code === OTP_MAX_ATTEMPTS_EXCEEDED_CODE;

const verifyErrorKeyForResponse = (error: unknown): OtpErrorKey => {
  const axiosError = toAxiosError(error);

  if (!axiosError) {
    return 'hostedValidation.otp.errors.generic';
  }

  // Terminal cases (max-attempts) short-circuit before reaching this
  // mapper. Anything that does reach here is a non-terminal error.
  const status = axiosError.response?.status;
  const reasons = axiosError.response?.data?.error?.reasons ?? [];

  if (status === 429) {
    return 'hostedValidation.otp.errors.rateLimit';
  }

  if (status === 422) {
    // BE puts the specific cause in `reasons[]`; `message` is just the generic 422 phrase.
    if (reasons.some((reason) => /no active otp/i.test(reason))) {
      return 'hostedValidation.otp.errors.expired';
    }

    return 'hostedValidation.otp.errors.invalidCode';
  }

  return 'hostedValidation.otp.errors.generic';
};

const sendErrorKeyForResponse = (error: unknown): OtpErrorKey =>
  toAxiosError(error)?.response?.status === 429
    ? 'hostedValidation.otp.errors.rateLimit'
    : 'hostedValidation.otp.errors.generic';

export const useOtp = ({
  onVerifySuccess,
  onMaxAttemptsExceeded,
}: {
  onVerifySuccess: (otpJwt: string) => void;
  onMaxAttemptsExceeded?: () => void;
}) => {
  const { getValues } = useFormContext<ValidationSubmissionArguments>();
  const { otpCooldownEndsAt, setOtpCooldown } = useOtpState();

  const [hasRequestedOtp, setHasRequestedOtp] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [verifyErrorKey, setVerifyErrorKey] = useState<OtpErrorKey | null>(null);
  const [sendErrorKey, setSendErrorKey] = useState<OtpErrorKey | null>(null);

  useEffect(() => {
    if (otpCooldownEndsAt === null) {
      setCooldownSeconds(0);

      return () => undefined;
    }

    const update = () => setCooldownSeconds(Math.max(0, Math.ceil((otpCooldownEndsAt - Date.now()) / 1000)));

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, [otpCooldownEndsAt]);

  const { mutate: mutateSendOtp, isLoading: isSending } = useMutation('sendOtp', sendOtpAction, {
    onError: (error) => {
      setSendErrorKey(sendErrorKeyForResponse(error));

      const axiosError = toAxiosError(error);

      if (axiosError?.response?.status === 429) {
        const retryAfter = parseInt(axiosError.response.headers?.['retry-after'] ?? '', 10);

        setOtpCooldown(Number.isFinite(retryAfter) ? retryAfter : OTP_COOLDOWN_SECONDS);
      }
    },
    onSuccess: () => {
      setSendErrorKey(null);
      setOtpCooldown(OTP_COOLDOWN_SECONDS);
    },
  });

  const { mutate: mutateVerifyOtp, isLoading: isVerifying } = useMutation('verifyOtp', verifyOtpAction, {
    onError: (error) => {
      if (isMaxAttemptsExceededError(error)) {
        onMaxAttemptsExceeded?.();

        return;
      }

      setVerifyErrorKey(verifyErrorKeyForResponse(error));
    },
    onSuccess: (response: ClientResponse<VerifyOtpResponse>) => {
      setVerifyErrorKey(null);
      onVerifySuccess(response.data.data.token);
    },
  });

  const sendOtp = useCallback(() => {
    setHasRequestedOtp(true);

    const { validationInviteId, email, firstName } = getValues();

    // Not reachable from the flow (the earlier steps require all three), but a silent
    // no-op here would leave the user staring at a step that never sent them anything.
    if (!validationInviteId || !email || !firstName) {
      setSendErrorKey('hostedValidation.otp.errors.generic');

      return;
    }

    mutateSendOtp({ validationInviteId, email, firstName });
  }, [getValues, mutateSendOtp]);

  const verifyOtp = useCallback(
    (code: string) => {
      const { validationInviteId, email } = getValues();

      if (!validationInviteId || !email) {
        return;
      }

      setSendErrorKey(null);
      mutateVerifyOtp({ validationInviteId, email, code });
    },
    [getValues, mutateVerifyOtp],
  );

  const clearVerifyError = useCallback(() => setVerifyErrorKey(null), []);
  const clearSendError = useCallback(() => setSendErrorKey(null), []);

  return {
    sendOtp,
    verifyOtp,
    clearVerifyError,
    clearSendError,
    cooldownSeconds,
    // Resend opens up once we've asked for a code and nothing is in flight. isSending
    // matters on top of the cooldown because the cooldown only starts when the request
    // resolves - without it a quick double press sends two codes.
    canResend: hasRequestedOtp && !isSending && cooldownSeconds === 0,
    isSending,
    isVerifying,
    verifyErrorKey,
    sendErrorKey,
  };
};
